import React, { useState } from "react";
import { FormikHelpers, useFormik } from "formik";
import { useLocation, useNavigate } from "react-router-dom";
import { createPasswordSchema } from "../validations/Schema";
import { UtilService } from "../services/shared/UtilService";
import { msgTypes } from "../constants/msgTypes";
import { MuiInput } from "../components/basicComponent/MuiInput";
import MuiButton from "../components/basicComponent/MuiButton";
import { CreateNewPasswordModel } from "../model/utilModels/CreateNewPasswordModel";
import { useSweetAlert } from "../services/customHooks/useSweetAlert";
import { Visibility, VisibilityOff } from "@mui/icons-material";

const CreatePassword: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { showSuccess, showError } = useSweetAlert();

  // Show/hide password toggles
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const email = location.state?.email || "";

  const onSubmit = async (
    values: CreateNewPasswordModel,
    { setSubmitting }: FormikHelpers<CreateNewPasswordModel>
  ) => {
    const payload = { ...values };
    setIsLoading(true);
    try {
      const response = await UtilService.createNewPassword(payload);

      if (response.statusMsg === msgTypes.SUCCESS.msg) {
        showSuccess("Password updated successfully!").then(() => {
          navigate("/");
        });
      } else {
        showError("Failed to update password. Please try again.");
        formik.resetForm();
        console.error("Login failed:", response);
      }
    } catch (error) {
      showError("Failed to update password. Please try again.");
      setIsLoading(false);
      console.error("An error occurred during login:", error);
    } finally {
      setSubmitting(false);
      setIsLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      ...new CreateNewPasswordModel(),
      email: email,
    },
    validationSchema: createPasswordSchema,
    onSubmit,
  });

  return (
    <main
      style={{ backgroundColor: "#fff" }}
      className="grow h-full d-flex content px-4 login-page "
      id="content"
      role="content"
    >
      <div className=" triangle-top-right">
        <img src="media/images/Group.png" alt="Group" />
      </div>
      <div className="login-container">
        <div className="decorative-triangle triangle-top-left"></div>
        <div className="login-form-section">
          <div className="logo-section m-auto">
            <img src="media/images/ewts-logo-(1).png" alt="logo" />
          </div>
          <div className="form-container m-auto">
            <h2 className="form-title ">Reset Password</h2>
            <form>
              <div className="input-group mb-3 ">
                {/* Current Password */}
                <MuiInput
                  placeholder="Enter Current Password"
                  isRequired={true}
                  isInvalid={
                    formik.touched.old_password && !!formik.errors.old_password
                  }
                  errorText={formik.errors.old_password}
                  onChange={(value) =>
                    formik.setFieldValue("old_password", value)
                  }
                  type={showOldPassword ? "text" : "password"}
                />
                <button
                  type="button"
                  onClick={() => setShowOldPassword((prev) => !prev)}
                  className="absolute  me-2  right-0 top-1/2 transform -translate-y-1/2 border-none bg-transparent"
                >
                  {showOldPassword ? (
                    <VisibilityOff
                      fontSize="small"
                      titleAccess="Hide password"
                    />
                  ) : (
                    <Visibility fontSize="small" titleAccess="Show password" />
                  )}
                </button>
              </div>
              <div className="input-group mb-3">
                {/* New Password */}
                <MuiInput
                  placeholder="Enter New Password"
                  isRequired={true}
                  isInvalid={
                    formik.touched.new_password && !!formik.errors.new_password
                  }
                  errorText={formik.errors.new_password}
                  onChange={(value) =>
                    formik.setFieldValue("new_password", value)
                  }
                  type={showNewPassword ? "text" : "password"}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword((prev) => !prev)}
                  className="absolute  me-2  right-0 top-1/2 transform -translate-y-1/2 border-none bg-transparent"
                >
                  {showNewPassword ? (
                    <VisibilityOff
                      fontSize="small"
                      titleAccess="Hide password"
                    />
                  ) : (
                    <Visibility fontSize="small" titleAccess="Show password" />
                  )}
                </button>
              </div>

              <div className="input-group mb-3">
                <MuiInput
                  placeholder="Enter Confirm Password"
                  isRequired={true}
                  isInvalid={
                    formik.touched.confirm_password &&
                    !!formik.errors.confirm_password
                  }
                  errorText={formik.errors.confirm_password}
                  onChange={(value) =>
                    formik.setFieldValue("confirm_password", value)
                  }
                  type={showConfirmPassword ? "text" : "password"}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                  className="absolute  me-2  right-0 top-1/2 transform -translate-y-1/2 border-none bg-transparent"
                >
                  {showConfirmPassword ? (
                    <VisibilityOff
                      fontSize="small"
                      titleAccess="Hide password"
                    />
                  ) : (
                    <Visibility fontSize="small" titleAccess="Show password" />
                  )}
                </button>
              </div>

              <div className="d-flex justify-content-center">
                <MuiButton
                  label="Submit"
                  variant="contained"
                  size="large"
                  onClick={() => formik.handleSubmit()}
                  className="login-button"
                  isLoading={isLoading}
                />
              </div>
            </form>
          </div>
        </div>

        <div className="image-section">
          {/* <div className="image-overlay"></div> */}
          <img src="./media/images/login-side-img.png" alt="login-side-img" />
        </div>
      </div>
    </main>

    // <main
    //   className="grow h-full d-flex content px-4 onboarding-sec"
    //   id="content"
    //   role="content"
    // >
    //   <div className="card login-form-sec">
    //     <div className="onboard-logo">
    //       <img src="media/images/ewts-logo.png" alt="logo" />
    //     </div>
    //     <h3 className="card-title">Reset Password</h3>
    //     <span className="text-2sm text-gray-700 forget-pass-title">
    //       Enter your new password
    //     </span>

    //     <div className="card-body">
    //       <div className="row">
    //         <div className="col-md-8 login-section">
    //           {/* Current Password */}
    //           <div className="flex flex-col gap-1 mb-4 relative">
    //             <MuiInput
    //               label="Enter Current Password"
    //               placeholder="Enter Current Password"
    //               isRequired={true}
    //               isInvalid={
    //                 formik.touched.old_password && !!formik.errors.old_password
    //               }
    //               errorText={formik.errors.old_password}
    //               onChange={(value) =>
    //                 formik.setFieldValue("old_password", value)
    //               }
    //               type={showOldPassword ? "text" : "password"}
    //             />
    //             <button
    //               type="button"
    //               onClick={() => setShowOldPassword((prev) => !prev)}
    //               className="absolute  me-2  right-0 top-1/2 transform -translate-y-1/2 border-none bg-transparent"
    //             >
    //               {showOldPassword ? (
    //                 <VisibilityOff
    //                   fontSize="small"
    //                   titleAccess="Hide password"
    //                 />
    //               ) : (
    //                 <Visibility fontSize="small" titleAccess="Show password" />
    //               )}
    //             </button>
    //           </div>

    //           {/* New Password */}
    //           <div className="flex flex-col gap-1 mb-4 relative create-pass-error">
    //             <MuiInput
    //               label="Enter New Password"
    //               placeholder="Enter New Password"
    //               isRequired={true}
    //               isInvalid={
    //                 formik.touched.new_password && !!formik.errors.new_password
    //               }
    //               errorText={formik.errors.new_password}
    //               onChange={(value) =>
    //                 formik.setFieldValue("new_password", value)
    //               }
    //               type={showNewPassword ? "text" : "password"}
    //             />
    //             <button
    //               type="button"
    //               onClick={() => setShowNewPassword((prev) => !prev)}
    //               className="absolute  me-2  right-0 top-1/2 transform -translate-y-1/2 border-none bg-transparent"
    //             >
    //               {showNewPassword ? (
    //                 <VisibilityOff
    //                   fontSize="small"
    //                   titleAccess="Hide password"
    //                 />
    //               ) : (
    //                 <Visibility fontSize="small" titleAccess="Show password" />
    //               )}
    //             </button>
    //           </div>

    //           {/* Confirm Password */}
    //           <div className="flex flex-col gap-1 mb-4 relative create-pass-error">
    //             <MuiInput
    //               label="Confirm Password"
    //               placeholder="Enter Confirm Password"
    //               isRequired={true}
    //               isInvalid={
    //                 formik.touched.confirm_password &&
    //                 !!formik.errors.confirm_password
    //               }
    //               errorText={formik.errors.confirm_password}
    //               onChange={(value) =>
    //                 formik.setFieldValue("confirm_password", value)
    //               }
    //               type={showConfirmPassword ? "text" : "password"}
    //             />
    //             <button
    //               type="button"
    //               onClick={() => setShowConfirmPassword((prev) => !prev)}
    //               className="absolute  me-2  right-0 top-1/2 transform -translate-y-1/2 border-none bg-transparent"
    //             >
    //               {showConfirmPassword ? (
    //                 <VisibilityOff
    //                   fontSize="small"
    //                   titleAccess="Hide password"
    //                 />
    //               ) : (
    //                 <Visibility fontSize="small" titleAccess="Show password" />
    //               )}
    //             </button>
    //           </div>

    //           {/* Submit Button */}
    //           <MuiButton
    //             label="Submit"
    //             variant="contained"
    //             size="large"
    //             onClick={() => formik.handleSubmit()}
    //             className="login-btn"
    //             isLoading={isLoading}
    //           />
    //         </div>
    //       </div>
    //     </div>
    //   </div>
    // </main>
  );
};

export default CreatePassword;
